/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MoreMenuButton, ZeroState } from '@modernloop/shared/components';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { CanDisable } from '@modernloop/shared/helper-components';
import { AvailabilityIcon, ErrorIcon, LockIcon, SendIcon, SuccessIcon, WarningIcon } from '@modernloop/shared/icons';
import { Button, MenuItem, Paper, PaperProps, Stack, StackProps, Theme, Typography } from '@mui/material';
import { isBefore, parseISO } from 'date-fns';

import {
  CandidateAvailabilityStatus_ApplicationFragment,
  CandidateAvailabilityStatus_AvailabilityFragment,
  CandidateAvailabilityStatus_AvailabilityRequestFragment,
  Maybe,
  useEmployeeByIdsLazyQuery,
} from 'src/generated/mloop-graphql';

import RequestAvailabilityPng from 'src/assets/images/request_availability_64.png';
import RequestSelfSchedulePng from 'src/assets/images/request_self_schedule.png';

import useDurationLabel from 'src/components/DurationLabel/useDurationLabel';
import Link from 'src/components/Link';

import useIsApplicationRejected from 'src/hooks/useIsApplicationRejected';

import { CandidateAvailabilityOptions } from 'src/store/slices/candidate-availability-options';

import IsoTimestamp, { assertIsoTimestamp } from 'src/types/IsoTimestamp';

import SelfScheduleDisableAlert from 'src/views-new/SelfSchedule/SelfScheduleDisableAlert';

import RecjectetCandidateAvailabilitySectionAlert from './RecjectetCandidateAvailabilityAlert';
import { isAvailabilitySufficient } from './utils';

enum CandidateAvailabilityStatusActions {
  CancelAvailabilityRequest = 'cancel-availability-request',
  EditAvailabilityRequest = 'request-availability-request',
}

const useSxProps = () => {
  return useMemo(() => {
    return {
      root: {
        borderRadius: (theme: Theme) => theme.spacing(1.5),
      },
      content: {
        flexGrow: 1,
      },
      action: {
        alignSelf: 'center',
        flexShrink: 0,
      },
      interviewScheduled: {
        paddingBottom: 1,
      },
    };
  }, []);
};

type Fragments = {
  application: CandidateAvailabilityStatus_ApplicationFragment;
  availability: CandidateAvailabilityStatus_AvailabilityFragment | undefined;
  availabilityRequest?: Maybe<CandidateAvailabilityStatus_AvailabilityRequestFragment>;
};

type Props = {
  taskId?: string;
  jobStageName: string;
  candidateTimezone?: string;
  options?: CandidateAvailabilityOptions;
  selfScheduleOptionsId?: string;
  lastApplicationStageCreatedAt?: IsoTimestamp;
  onRequestAvailability: () => void;
  onReShareAvailabilityRequest: () => void;
  onDeleteAvailabilityRequest: () => void;
  availabilityRequestActionsJSX: JSX.Element | null;
};

const CandidateAvailabilityStatus: FCWithFragments<Fragments, Props> = ({
  taskId,
  jobStageName,
  candidateTimezone,
  options,
  selfScheduleOptionsId,
  lastApplicationStageCreatedAt,
  onRequestAvailability,
  onReShareAvailabilityRequest,
  onDeleteAvailabilityRequest,
  availabilityRequestActionsJSX,
  application,
  availability,
  availabilityRequest,
}) => {
  const { t } = useTranslation();
  const sxProps = useSxProps();
  const [showSelfScheduleDeleteAlert, setShowSelfScheduleDeleteAlert] = useState(false);
  const [fetchEmployeeById, { data }] = useEmployeeByIdsLazyQuery();
  const durationLabel = useDurationLabel(
    availability?.updatedAt ? assertIsoTimestamp(availability.updatedAt) : undefined
  );
  const isApplicationRejected = useIsApplicationRejected(application.status);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (!options?.employeeId) {
      return;
    }
    fetchEmployeeById({ variables: { input: [options.employeeId] } });
  }, [options, fetchEmployeeById]);

  let paperColor: PaperProps['color'];
  let icon = <></>;
  let content = <></>;
  let action = <></>;
  const menuOptions = (
    <>
      <MenuItem key={CandidateAvailabilityStatusActions.EditAvailabilityRequest} onClick={onRequestAvailability}>
        {t('hiring-dashboard-candidates:candidates.availabilityStatus.menuEdit')}
      </MenuItem>
      <MenuItem
        key={CandidateAvailabilityStatusActions.CancelAvailabilityRequest}
        onClick={onDeleteAvailabilityRequest}
      >
        <Typography color="error">
          {t('hiring-dashboard-candidates:candidates.availabilityStatus.menuDelete')}
        </Typography>
      </MenuItem>
    </>
  );

  let alignItems: StackProps['alignItems'];
  let selfScheduleMessage = <></>;
  let insufficientAvailabilityMessage = <></>;

  if (selfScheduleOptionsId) {
    if (taskId && !availability?.rcEnteredAvailability?.length && !availability?.candidateEnteredAvailability?.length) {
      return (
        <ZeroState
          icon={
            <img
              src={RequestSelfSchedulePng}
              alt={t('hiring-dashboard-candidates:candidates.availabilityStatus.selfScheduleIsOn')}
              width="64px"
              height="64px"
            />
          }
          label={
            <Stack alignItems="center">
              <Typography fontWeight={600}>
                {t('hiring-dashboard-candidates:candidates.availabilityStatus.selfScheduleIsOn')}
              </Typography>
              <Typography color="text.secondary">
                {t('hiring-dashboard-candidates:candidates.availabilityStatus.selfScheduleCloseMessage')}
              </Typography>
            </Stack>
          }
        />
      );
    }

    selfScheduleMessage = (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ paddingBottom: (theme) => theme.spacing(1) }}>
        <LockIcon />
        <Typography color="text.secondary" variant="body2">
          {t('hiring-dashboard-candidates:candidates.availabilityStatus.enableAvailabilityRequests')}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link color="primary" onClick={() => setShowSelfScheduleDeleteAlert(true)} variant="body2">
            {t('hiring-dashboard-candidates:candidates.availabilityStatus.closeRequest')}
          </Link>
        </Typography>
      </Stack>
    );
  }

  if (
    taskId &&
    !options?.externalId &&
    !availability?.rcEnteredAvailability?.length &&
    !availability?.candidateEnteredAvailability?.length
  ) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <img
          src={RequestAvailabilityPng}
          alt={t('hiring-dashboard-candidates:candidates.availabilityStatus.requestAvailabilityTitle')}
          width="64px"
          height="64px"
        />
        <Stack spacing="0.5">
          <Typography fontWeight={600}>
            {t('hiring-dashboard-candidates:candidates.availabilityStatus.requestAvailabilityTitle')}
          </Typography>
          <Typography color="text.secondary">
            {t('hiring-dashboard-candidates:candidates.availabilityStatus.requestAvailabilityDescription')}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  const hasCandidateSubmittedAvailabilityAfterRequestCreated =
    availability?.candidateUpdatedAt &&
    options?.createdAt &&
    parseISO(availability.candidateUpdatedAt).getTime() > parseISO(options.createdAt).getTime();

  const isInterviewScheduledAfterRequestCreated =
    lastApplicationStageCreatedAt &&
    options?.createdAt &&
    parseISO(lastApplicationStageCreatedAt).getTime() > parseISO(options.createdAt).getTime();

  if (
    !options?.externalId &&
    (availability?.rcEnteredAvailability?.length || isInterviewScheduledAfterRequestCreated || selfScheduleOptionsId)
  ) {
    paperColor = 'background.contrast';
    icon = <AvailabilityIcon />;
    content = (
      <Typography color="text.secondary" sx={sxProps.content} fontWeight={600}>
        {t('hiring-dashboard-candidates:candidates.availabilityStatus.requestMoreAvailability', { jobStageName })}
      </Typography>
    );
    action = (
      <Button
        data-testid="candidate-details-candidate-availability-request"
        sx={sxProps.action}
        onClick={onRequestAvailability}
      >
        {t('hiring-dashboard-candidates:candidates.availabilityStatus.request')}
      </Button>
    );
    alignItems = 'center';
  } else if (options?.externalId && hasCandidateSubmittedAvailabilityAfterRequestCreated) {
    paperColor = 'background.contrast';
    icon = <SuccessIcon color="success" />;
    content = (
      <Stack sx={sxProps.content}>
        <Typography data-testid="candidate-availability-received-status" color="success.main" fontWeight={600}>
          {t('hiring-dashboard-candidates:candidates.availabilityStatus.availabilityReceived')}
        </Typography>

        <Typography color="text.secondary" variant="body2">
          {t('hiring-dashboard-candidates:candidates.availabilityStatus.requestStillOpenDescription')}
        </Typography>
        {availabilityRequestActionsJSX}
      </Stack>
    );

    action = (
      <Stack direction="row" alignItems="center" sx={sxProps.action} spacing={1}>
        <Button onClick={onReShareAvailabilityRequest}>
          {t('hiring-dashboard-candidates:candidates.availabilityStatus.reShareLink')}
        </Button>
        <MoreMenuButton>{menuOptions}</MoreMenuButton>
      </Stack>
    );

    const hasRCUpdatedAvailabilityAfterCandidate =
      availability?.candidateUpdatedAt &&
      availability.employeeUpdatedAt &&
      parseISO(availability.candidateUpdatedAt).getTime() < parseISO(availability.employeeUpdatedAt).getTime();

    const sufficient = isAvailabilitySufficient(
      options,
      availability.candidateEnteredAvailability || [],
      candidateTimezone || getLocalTimezone(),
      availability.updatedAt
    );

    if (!hasRCUpdatedAvailabilityAfterCandidate && !sufficient) {
      paperColor = 'warning.tint';

      insufficientAvailabilityMessage = (
        <Stack direction="row" spacing={1} sx={{ paddingBottom: (theme) => theme.spacing(1) }}>
          <WarningIcon color="warning" />
          <Typography color="text.secondary" variant="body2">
            {t('hiring-dashboard-candidates:candidates.availabilityStatus.insufficientAvailability', { jobStageName })}
          </Typography>
        </Stack>
      );
    }
  } else if (options?.externalId) {
    if (isApplicationRejected) {
      paperColor = 'error.tint';
      icon = <ErrorIcon color="error" />;
      content = (
        <RecjectetCandidateAvailabilitySectionAlert
          heading={t('hiring-dashboard-candidates:candidates.availabilityStatus.requestCannotBeFulfilled')}
          availabilityRequest={availabilityRequest}
          actionsJSX={availabilityRequestActionsJSX}
        />
      );
      action = <></>;
    } else {
      const sentBy = data?.employeeByIds?.length
        ? t('hiring-dashboard-candidates:candidates.availability.addedByEmployee', {
            employeeName: data?.employeeByIds[0].fullName,
          })
        : '';
      paperColor = taskId ? 'warning.tint' : 'background.contrast';
      icon = <SendIcon color={taskId ? 'warning' : undefined} />;
      content = (
        <Stack sx={sxProps.content}>
          <Typography color="text.secondary" fontWeight={600}>
            {t('hiring-dashboard-candidates:candidates.availabilityStatus.waitingForCandidate')}
          </Typography>

          {options.lastCandidateCommunicationSentAt && (
            <Typography color="text.secondary" variant="body2">
              {t('hiring-dashboard-candidates:candidates.availabilityStatus.linkSentBy', {
                sentBy,
                durationLabel,
              })}
            </Typography>
          )}
          {availabilityRequestActionsJSX}
        </Stack>
      );
      action = (
        <Stack direction="row" alignItems="center" sx={sxProps.action} spacing={1}>
          <Button onClick={onReShareAvailabilityRequest}>
            {t('hiring-dashboard-candidates:candidates.availabilityStatus.reShareLink')}
          </Button>
          <MoreMenuButton>{menuOptions}</MoreMenuButton>
        </Stack>
      );
    }
  } else {
    paperColor = 'info.tint';
    icon = <AvailabilityIcon color="info" />;
    content = (
      <Stack sx={sxProps.content} spacing={0.25}>
        <Typography color="primary" fontWeight={600}>
          {t('hiring-dashboard-candidates:candidates.availabilityStatus.requestAvailabilityFromCandidate', {
            jobStageName,
          })}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {t('hiring-dashboard-candidates:candidates.availabilityStatus.requestAvailabilityInstructions')}
        </Typography>
      </Stack>
    );
    action = (
      <Button
        data-testid="candidate-details-candidate-availability-request"
        sx={sxProps.action}
        onClick={onRequestAvailability}
      >
        {t('hiring-dashboard-candidates:candidates.availabilityStatus.request')}
      </Button>
    );
  }

  return (
    <>
      <Paper sx={[sxProps.root, { backgroundColor: paperColor, padding: 1.5 }]}>
        {isInterviewScheduledAfterRequestCreated && Boolean(options?.externalId) && !taskId && (
          <Typography color="text.secondary" variant="body2" sx={sxProps.interviewScheduled}>
            {t('hiring-dashboard-candidates:candidates.availabilityStatus.candidateScheduled')}
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link color="primary" variant="body2" onClick={onDeleteAvailabilityRequest}>
              {t('hiring-dashboard-candidates:candidates.availabilityStatus.closeThisRequest')}
            </Link>
          </Typography>
        )}
        {!isInterviewScheduledAfterRequestCreated &&
          hasCandidateSubmittedAvailabilityAfterRequestCreated &&
          options?.externalId &&
          availability?.candidateUpdatedAt &&
          availability.employeeUpdatedAt &&
          isBefore(parseISO(availability.candidateUpdatedAt), parseISO(availability.employeeUpdatedAt)) && (
            <Typography color="text.secondary" variant="body2" sx={sxProps.interviewScheduled}>
              {t('hiring-dashboard-candidates:candidates.availabilityStatus.availabilityOverwritten', {
                employeeName: availability.lastUpdatedByEmployee?.fullName,
              })}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link color="primary" variant="body2" onClick={onDeleteAvailabilityRequest}>
                {t('hiring-dashboard-candidates:candidates.availabilityStatus.closeThisRequest')}
              </Link>
            </Typography>
          )}
        {selfScheduleMessage}
        {insufficientAvailabilityMessage}
        <CanDisable disabled={Boolean(selfScheduleOptionsId)}>
          <Stack direction="row" alignItems={alignItems} spacing={1}>
            {icon}
            {content}
            {!taskId && action}
          </Stack>
        </CanDisable>
      </Paper>
      {showSelfScheduleDeleteAlert && selfScheduleOptionsId && (
        <SelfScheduleDisableAlert
          selfScheduleOptionsId={selfScheduleOptionsId}
          onClose={() => setShowSelfScheduleDeleteAlert(false)}
          onDelete={() => setShowSelfScheduleDeleteAlert(false)}
        />
      )}
    </>
  );
};

CandidateAvailabilityStatus.fragments = {
  application: gql`
    fragment CandidateAvailabilityStatus_application on Application {
      id
      status
    }
  `,
  availability: gql`
    fragment CandidateAvailabilityStatus_availability on CandidateAvailability {
      id
      candidateEnteredAvailability {
        startAt
        endAt
      }
      rcEnteredAvailability {
        startAt
        endAt
      }
      lastUpdatedByEmployee {
        id
        fullName
      }
      employeeUpdatedAt
      candidateUpdatedAt
      updatedAt
    }
  `,
  availabilityRequest: gql`
    ${RecjectetCandidateAvailabilitySectionAlert.fragments.availabilityRequest}
    fragment CandidateAvailabilityStatus_availabilityRequest on CandidateAvailabilityOptions {
      ...RecjectetCandidateAvailabilitySectionAlert_availabilityRequest
    }
  `,
};

export default CandidateAvailabilityStatus;
